import { FormEvent, useCallback, useState } from 'react';
import useTableOrder from 'hooks/tableOrder';
import { User } from 'types/user';
import { api } from 'services/api';
import { Paginated } from 'types/paginated';

export type UsersQueryParams = {
  active: boolean;
  name: string;
  document_number: string;
};

export function useFetchUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);
  const [orderedIndex, sort] = useTableOrder();
  const [filter, setFilter] = useState<UsersQueryParams>({
    document_number: '',
    name: '',
    active: true,
  });

  function fetchData(nextPage = page, nextRows = rows, nextFilter = filter) {
    setLoading(true);

    api
      .get<Paginated<User[]>>('/users', {
        params: {
          ...nextFilter,
          active: nextFilter.active ? 1 : 0,
          page: nextPage + 1,
          rows: nextRows,
        },
      })
      .then(response => {
        setTotal(response.data.total);
        const _users = response.data.items.map(user => ({
          ...user,
          formattedActive: user.active ? 'Sim' : 'Não',
          formattedProfileName: user.user_profile ? user.user_profile.name : '',
        }));
        setUsers(_users);
      })
      .finally(() => setLoading(false));
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchData(0, rows, filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
    fetchData(nextPage, rows, filter);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
    fetchData(0, nextRows, filter);
  }

  function handleSort(index: string) {
    const p = sort(index, users);
    setUsers(p);
  }

  function handleChange<K extends keyof UsersQueryParams>(index: K, value: UsersQueryParams[K]) {
    setFilter(state => ({
      ...state,
      [index]: value,
    }));
  }

  return {
    users,
    loading,
    orderedIndex,
    handleSort,
    filter,
    handleChange,
    setPage: handleChangePage,
    setRows: handleChangeRows,
    handleSearch,
    page,
    rows,
    total,
  };
}
